import React from 'react'

import { AcornsIcon } from '@acorns/icons'
import { GenericAnalyticsClient, withAnalyticsClient } from '@acorns/web-utils'
import { compose, withHandlers, withState } from 'recompose'
import styled from 'styled-components'

import { IconDropdownMenu } from 'components/DropDownMenu'
import {
  FEEDBACK_THUMBS_DOWN_BUTTON,
  FEEDBACK_THUMBS_UP_BUTTON,
} from 'utils/testing/testIDs'

import { ButtonTappedFields } from './segment'

const FeedbackContainer = styled.div`
  align-items: center;
  display: flex;
  margin-top: 40px;
`

const Text = styled.p`
  font-size: 14px;
  font-weight: 600;
  margin: 0;
`

const ActionButtons = styled.div`
  margin-left: 16px;
`

const ActionButtonContainer = styled.div`
  display: inline-block;
  height: 24px;
  position: relative;
  width: 24px;

  &:not(:last-child) {
    margin-right: 12px;
  }
`

const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
`

const NegativeFeedbackContainer = styled.div`
  display: inline-block;
  position: relative;
`

type ActionProps = {
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void
  iconType: JSX.Element
  testId: string
}

type Props = {
  articleId?: string
  articleTitle?: string
  articleType?: string
  negativeFeedbackReasons?: string[]
}

type StateProps = {
  isCollectingFeedbackReason: boolean
  feedbackCollected: boolean
}

type StateHandlerProps = {
  setIsCollectingFeedbackReason: (isCollectingFeedbackReason: boolean) => void
  setFeedbackCollected: (feedbackCollected: boolean) => void
}

type HandlerProps = {
  handleSelectedAction: (
    objectName: 'articleRatedPositive' | 'articleRatedNegative',
  ) => void
  handleLiked: (event: React.MouseEvent<HTMLButtonElement>) => void
  handleDisliked: (event: React.MouseEvent<HTMLButtonElement>) => void
  handleDropdownClosed: () => void
  handleNegativeFeedbackReason: (reason: string) => void
  handleDropdownOpened: () => void
}

type Analytics = {
  analytics: GenericAnalyticsClient
}

type InnerProps = Props &
  Analytics &
  HandlerProps &
  StateProps &
  StateHandlerProps

const PROMPT = 'Was this article helpful?'

const enhance = compose<InnerProps, Props>(
  withAnalyticsClient,
  withState('feedbackCollected', 'setFeedbackCollected', false),
  withState(
    'isCollectingFeedbackReason',
    'setIsCollectingFeedbackReason',
    false,
  ),
  withHandlers<InnerProps, Partial<HandlerProps>>({
    handleSelectedAction:
      ({
        analytics,
        articleId,
        articleTitle,
        articleType,
      }: {
        analytics: GenericAnalyticsClient
        articleId?: string
        articleTitle?: string
        articleType?: string
      }) =>
      (objectName) => {
        analytics.track(
          'Button Tapped',
          ButtonTappedFields({
            articleId,
            articleTitle,
            articleType,
            objectName,
            title: PROMPT,
          }),
        )
      },
    handleNegativeFeedbackReason:
      ({
        analytics,
        articleId,
        articleTitle,
        articleType,
        negativeFeedbackReasons,
        setFeedbackCollected,
      }) =>
      (index) => {
        const parsedIndex = parseInt(index, 10)
        const feedbackReason =
          negativeFeedbackReasons && negativeFeedbackReasons[parsedIndex]

        analytics.track('Button Tapped', {
          article_id: articleId,
          article_title: articleTitle,
          article_type: articleType,
          cta_title: feedbackReason,
          event_text: 'Button Tapped',
          event: 'button_tapped',
          object_name: 'growArticleRatedReason',
          screen: 'growArticle',
          screen_name: 'growArticle',
        })
        setFeedbackCollected(true)
      },
  }),
  withHandlers<InnerProps, Partial<HandlerProps>>({
    handleLiked:
      ({ handleSelectedAction, setFeedbackCollected }) =>
      () => {
        handleSelectedAction('articleRatedPositive')
        setFeedbackCollected(true)
      },
    handleDisliked:
      ({
        handleSelectedAction,
        negativeFeedbackReasons,
        setFeedbackCollected,
      }) =>
      () => {
        handleSelectedAction('articleRatedNegative')

        if (!negativeFeedbackReasons || negativeFeedbackReasons.length === 0) {
          setFeedbackCollected(true)
        }
      },
    handleDropdownOpened:
      ({ setIsCollectingFeedbackReason }) =>
      () => {
        setIsCollectingFeedbackReason(true)
      },
    handleDropdownClosed:
      ({ setFeedbackCollected }) =>
      () => {
        setFeedbackCollected(true)
      },
  }),
)

const Action = ({ onClick, iconType, testId }: ActionProps) => (
  <ActionButtonContainer>
    <ActionButton data-testid={testId} onClick={onClick}>
      {iconType}
    </ActionButton>
  </ActionButtonContainer>
)

const FeedbackSubmitted = () => <Text>Thanks for your feedback!</Text>

const Feedback = ({
  feedbackCollected,
  handleLiked,
  handleDisliked,
  handleDropdownClosed,
  handleDropdownOpened,
  handleNegativeFeedbackReason,
  negativeFeedbackReasons,
  isCollectingFeedbackReason,
}: InnerProps) => (
  <FeedbackContainer>
    {feedbackCollected ? (
      <FeedbackSubmitted />
    ) : (
      <>
        <Text>{PROMPT}</Text>
        <ActionButtons>
          <Action
            iconType={
              <AcornsIcon icon={AcornsIcon.Icon.ThumbsUp} width="24px" />
            }
            onClick={handleLiked}
            testId={FEEDBACK_THUMBS_UP_BUTTON}
          />
          {negativeFeedbackReasons && negativeFeedbackReasons.length > 0 ? (
            <NegativeFeedbackContainer>
              <IconDropdownMenu
                options={negativeFeedbackReasons.map(
                  (reason: string, index: number) => ({
                    label: reason,
                    value: `${index}`,
                  }),
                )}
                onChange={handleNegativeFeedbackReason}
                onClose={handleDropdownClosed}
                onOpen={handleDropdownOpened}
              >
                <Action
                  iconType={
                    isCollectingFeedbackReason ? (
                      <AcornsIcon
                        icon={AcornsIcon.Icon.ThumbsDownFilled}
                        width="24px"
                      />
                    ) : (
                      <AcornsIcon
                        icon={AcornsIcon.Icon.ThumbsDown}
                        width="24px"
                      />
                    )
                  }
                  onClick={handleDisliked}
                  testId={FEEDBACK_THUMBS_DOWN_BUTTON}
                />
              </IconDropdownMenu>
            </NegativeFeedbackContainer>
          ) : (
            <Action
              iconType={
                <AcornsIcon icon={AcornsIcon.Icon.ThumbsDown} width="24px" />
              }
              onClick={handleDisliked}
              testId={FEEDBACK_THUMBS_DOWN_BUTTON}
            />
          )}
        </ActionButtons>
      </>
    )}
  </FeedbackContainer>
)

export default enhance(Feedback)
